
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/features/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/features/shared/components/ui/select';
import { Search } from 'lucide-react';

interface EmployeeSearchFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  departmentFilter: string;
  onDepartmentChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  // Real departments present in the directory (drives the dropdown options).
  departments?: string[];
}

export const EmployeeSearchFilters: React.FC<EmployeeSearchFiltersProps> = ({
  searchTerm,
  onSearchChange,
  departmentFilter,
  onDepartmentChange,
  statusFilter,
  onStatusChange,
  departments = [],
}) => {
  const { t } = useTranslation(['employees']);

  return (
    <div className="flex flex-col md:flex-row gap-4">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 w-4 h-4" />
        <Input
          placeholder={t('employees:search_employees')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 border-orange-200 dark:border-orange-500/25 focus:border-orange-400 focus:ring-orange-400"
        />
      </div>
      <Select value={departmentFilter} onValueChange={onDepartmentChange}>
        <SelectTrigger className="w-full md:w-48 border-orange-200 dark:border-orange-500/25 focus:border-orange-400">
          <SelectValue placeholder={t('employees:filter_by_department')} />
        </SelectTrigger>
        <SelectContent className="border-orange-200 dark:border-orange-500/25">
          <SelectItem value="all" className="hover:bg-orange-50">All Departments</SelectItem>
          {departments.map((dept) => (
            <SelectItem key={dept} value={dept} className="hover:bg-orange-50">{dept}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={onStatusChange}>
        <SelectTrigger className="w-full md:w-48 border-orange-200 dark:border-orange-500/25 focus:border-orange-400">
          <SelectValue placeholder={t('employees:filter_by_status')} />
        </SelectTrigger>
        <SelectContent className="border-orange-200 dark:border-orange-500/25">
          <SelectItem value="all" className="hover:bg-orange-50">All Status</SelectItem>
          <SelectItem value="active" className="hover:bg-orange-50">{t('employees:status.active')}</SelectItem>
          <SelectItem value="on_leave" className="hover:bg-orange-50">{t('employees:status.on_leave')}</SelectItem>
          <SelectItem value="inactive" className="hover:bg-orange-50">{t('employees:status.inactive')}</SelectItem>
          <SelectItem value="expired_contract" className="hover:bg-orange-50">{t('employees:status.expired_contract', { defaultValue: 'Expired contract' })}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
